import { useState } from "react";
import { Link } from "react-router-dom";
import { Portal } from "../portal";
import { isAgeVerified, setAgeVerified } from "@/lib/nsfw-check";

const SITE_AGE_GATE_ID = "site-age-gate";

/**
 * Where to send users who decline the age gate. Kept as a normal external
 * redirect so declining actually leaves the site (rather than staying on it).
 */
const LEAVE_SITE_URL = "https://www.google.com";

/**
 * Site-wide 18+ confirmation gate. Renders a blocking modal on landing when
 * the visitor hasn't previously confirmed they're an adult. The answer is
 * persisted in localStorage (via `setAgeVerified`) so it only shows once.
 */
export function SiteAgeGate() {
  const [verified, setVerified] = useState(isAgeVerified);

  if (verified) return null;

  const handleConfirm = () => {
    setAgeVerified();
    setVerified(true);
  };

  const handleDecline = () => {
    window.location.href = LEAVE_SITE_URL;
  };

  return (
    <Portal>
      <div
        id={SITE_AGE_GATE_ID}
        role="dialog"
        aria-modal="true"
        aria-labelledby="site-age-gate-title"
        className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
      >
        <div className="w-full max-w-md rounded-2xl border border-white/[0.1] bg-gradient-to-b from-[#111] to-[#0a0a0a] p-8 shadow-2xl">
          <p className="text-center text-lg font-semibold tracking-tight mb-6">
            <span className="text-white">pixelmaker</span>
            <span className="text-violet-400">.ai</span>
          </p>

          <h2
            id="site-age-gate-title"
            className="text-xl sm:text-2xl font-bold text-white text-center tracking-wide mb-3"
          >
            ADULTS ONLY
          </h2>

          <p className="text-[#888] text-center mb-8">
            By entering, you confirm you are at least 18 years old and agree to
            our{" "}
            <Link
              to="/terms"
              className="text-white underline underline-offset-2 hover:text-violet-300 transition-colors"
            >
              Terms
            </Link>
            .
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleConfirm}
              className="w-full py-3.5 px-4 rounded-full bg-gradient-to-r from-violet-600 to-violet-500 text-white font-semibold hover:from-violet-500 hover:to-violet-400 transition-all"
            >
              I'm over 18
            </button>
            <button
              onClick={handleDecline}
              className="w-full py-2 px-4 text-[#888] font-medium hover:text-white transition-colors"
            >
              Leave site
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
